import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  InputAdornment,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Receipt as ReceiptIcon,
  Calculate as CalculateIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  department: string;
}

interface ExpenseCategory {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
}

interface CreateExpenseDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  employees: Employee[];
  categories: ExpenseCategory[];
}

const VAT_RATES = [
  { value: 'STANDARD', label: 'Standard (20%)', rate: 0.20 },
  { value: 'REDUCED', label: 'Reduced (5%)', rate: 0.05 },
  { value: 'ZERO', label: 'Zero (0%)', rate: 0.00 },
  { value: 'EXEMPT', label: 'Exempt', rate: 0.00 }
];

const CreateExpenseDialog: React.FC<CreateExpenseDialogProps> = ({
  open,
  onClose,
  onSuccess,
  employees,
  categories
}) => {
  const [formData, setFormData] = useState({
    employee_id: '',
    category_id: '',
    expense_date: new Date(),
    description: '',
    net_amount: 0,
    vat_rate: 'STANDARD',
    merchant_name: '',
    business_purpose: '',
    notes: ''
  });

  const [calculations, setCalculations] = useState({
    vat_amount: 0,
    total_amount: 0
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    calculateAmounts();
  }, [formData.net_amount, formData.vat_rate]);

  const calculateAmounts = () => {
    const netAmount = formData.net_amount || 0;
    const vatRate = VAT_RATES.find(rate => rate.value === formData.vat_rate)?.rate || 0;
    const vatAmount = netAmount * vatRate;
    const totalAmount = netAmount + vatAmount;

    setCalculations({
      vat_amount: vatAmount,
      total_amount: totalAmount
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      // Validate required fields
      if (!formData.employee_id) {
        setError('Please select an employee');
        return;
      }

      if (!formData.category_id) {
        setError('Please select a category');
        return;
      }

      if (!formData.description.trim()) {
        setError('Please enter a description');
        return;
      }

      if (formData.net_amount <= 0) {
        setError('Please enter a valid amount');
        return;
      }

      const expenseData = {
        employee_id: parseInt(formData.employee_id),
        category_id: parseInt(formData.category_id),
        expense_date: formData.expense_date.toISOString().split('T')[0],
        description: formData.description.trim(),
        net_amount: formData.net_amount,
        vat_rate: formData.vat_rate,
        merchant_name: formData.merchant_name.trim() || null,
        business_purpose: formData.business_purpose.trim() || null,
        notes: formData.notes.trim() || null
      };

      const response = await fetch('/api/v1/expenses/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(expenseData),
      });

      if (response.ok) {
        onSuccess();
        handleClose();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create expense');
      }
    } catch (error) {
      console.error('Error creating expense:', error);
      setError('Failed to create expense');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setFormData({
      employee_id: '',
      category_id: '',
      expense_date: new Date(),
      description: '',
      net_amount: 0,
      vat_rate: 'STANDARD',
      merchant_name: '',
      business_purpose: '',
      notes: ''
    });
    setError('');
    onClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <ReceiptIcon />
            Add New Expense
          </Box>
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Basic Information */}
          <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
            Basic Information
          </Typography>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Employee</InputLabel>
                <Select
                  value={formData.employee_id}
                  label="Employee"
                  onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                >
                  {employees.map((employee) => (
                    <MenuItem key={employee.id} value={employee.id.toString()}>
                      {employee.first_name} {employee.last_name} - {employee.department}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category_id}
                  label="Category"
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id.toString()}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <DatePicker
                label="Expense Date"
                value={formData.expense_date}
                onChange={(date) => setFormData({ ...formData, expense_date: date || new Date() })}
                slotProps={{
                  textField: { fullWidth: true, required: true }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Merchant/Supplier"
                value={formData.merchant_name}
                onChange={(e) => setFormData({ ...formData, merchant_name: e.target.value })}
                placeholder="e.g., Tesco, Amazon, etc."
              />
            </Grid>
          </Grid>

          {/* Expense Details */}
          <Typography variant="h6" gutterBottom>
            Expense Details
          </Typography>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of the expense"
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                label="Net Amount"
                type="number"
                value={formData.net_amount}
                onChange={(e) => setFormData({ ...formData, net_amount: parseFloat(e.target.value) || 0 })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Sales Tax Rate</InputLabel>
                <Select
                  value={formData.vat_rate}
                  label="Sales Tax Rate"
                  onChange={(e) => setFormData({ ...formData, vat_rate: e.target.value })}
                >
                  {VAT_RATES.map((rate) => (
                    <MenuItem key={rate.value} value={rate.value}>
                      {rate.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Business Purpose"
                value={formData.business_purpose}
                onChange={(e) => setFormData({ ...formData, business_purpose: e.target.value })}
                placeholder="Business justification"
              />
            </Grid>
          </Grid>

          {/* Calculations */}
          <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <CalculateIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Calculations
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Box>
                    <Typography variant="body2" color="textSecondary">Net Amount</Typography>
                    <Typography variant="h6">${formData.net_amount.toFixed(2)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box>
                    <Typography variant="body2" color="textSecondary">Sales Tax Amount</Typography>
                    <Typography variant="h6">${calculations.vat_amount.toFixed(2)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box>
                    <Typography variant="body2" color="textSecondary">Total Amount</Typography>
                    <Typography variant="h6" color="primary">${calculations.total_amount.toFixed(2)}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Receipt Upload */}
          <Card sx={{ mb: 3, border: '2px dashed', borderColor: 'grey.300' }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <UploadIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Upload Receipt
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Drag and drop your receipt here, or click to browse
              </Typography>
              <Button variant="outlined" component="label">
                Choose File
                <input type="file" hidden accept="image/*,.pdf" />
              </Button>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Supported formats: JPG, PNG, PDF (Max 10MB)
              </Typography>
            </CardContent>
          </Card>

          {/* Additional Notes */}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Additional Notes (Optional)"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Any additional information about this expense..."
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={loading}
          >
            Save as Draft
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Submit Expense'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default CreateExpenseDialog;
